import { Radio, Select } from '@mantine/core'
import type { TValuesCurrentPoints, TValuesCurrentProperties } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { SharedSettings } from 'settings/SharedSettings'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { mapSelectedCardStore } from 'stores/mapSelectedCardStore/mapSelectedCardStore'
import type { TAppliedIrrigationActivityDisplayOption } from 'stores/smallStore/smallStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'
import { useIrrigationSelectData } from '../_utils/useIrrigationSelectData'
import { EmitterMultiSelect } from './EmitterMultiSelect'
import { IrrigationActivityZonesInfoText } from './IrrigationActivityZonesInfoText'

const updateMapVisuals = (newValue: string | null) => {
  if (newValue === null) return

  const newValueType = newValue as TValuesCurrentPoints

  mapControlsStore.setState((s) => ({
    ...s,
    mapVisualValueGroup: {
      ...s.mapVisualValueGroup,
      [MAP_VISUAL.PROPERTY]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.PROPERTY],
        irrigation_activity_zones: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.PROPERTY].irrigation_activity_zones ?? {
            mItem: 'M_Property',
          }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentProperties,
        },
      },
      [MAP_VISUAL.IRRIGATION_ZONE]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.IRRIGATION_ZONE],
        irrigation_activity_zones: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.IRRIGATION_ZONE]?.irrigation_activity_zones ?? {
            mItem: 'M_IrrigationZone',
          }),
          valueType: newValueType,
        },
      },
    },
  }))
}

const radioStyles = {
  label: {
    color: HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD ? colors.midnight : colors.white,
    cursor: 'pointer',
  },
  radio: { cursor: 'pointer' },
}

const VOLUME: TAppliedIrrigationActivityDisplayOption = 'VOLUME'
const DURATION: TAppliedIrrigationActivityDisplayOption = 'DURATION'

export const IrrigationActivityZonesSecondaryDropdown = () => {
  const isWideScreen = useIsWideScreen()
  const selectClickToggle = useSelectClickToggle()
  const data = useIrrigationSelectData()
  const appliedIrrigationActivityDisplay = smallStore.useSelector((s) => s.appliedIrrigationActivityDisplay)
  const precipitationUnit = userDetailsStore.useSelector((s) => s?.rain)

  const { selectedValueType, selectedEmitters } = mapControlsStore.useSelector((s) => {
    return {
      selectedValueType:
        s.mapVisualValueGroup?.[MAP_VISUAL.IRRIGATION_ZONE]?.irrigation_activity_zones?.valueType,
      selectedEmitters: s.selectedEmitters,
    }
  })

  const isDurationOrApplied = !!(
    selectedValueType &&
    !selectedValueType?.endsWith('_status') &&
    !selectedValueType?.endsWith('_interval')
  )

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      <div
        onClick={() => mapSelectedCardStore.actions.hide()}
        css={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <Select
          {...selectClickToggle}
          data={data}
          filter={mantineSelectSearchFilterProp}
          onChange={updateMapVisuals}
          styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
          searchable={isWideScreen}
          value={selectedValueType}
          comboboxProps={{ withinPortal: true }}
        />
        <div css={{ maxWidth: 300 }}>
          <EmitterMultiSelect />
        </div>
      </div>
      {isDurationOrApplied && (
        <Radio.Group
          css={{ marginTop: 12 }}
          onChange={(newVal) =>
            smallStore.setState((s) => ({
              ...s,
              appliedIrrigationActivityDisplay: newVal as TAppliedIrrigationActivityDisplayOption,
            }))
          }
          value={appliedIrrigationActivityDisplay}
        >
          <div css={{ display: 'flex', flexWrap: 'wrap', gap: '0px 12px' }}>
            <Radio
              label={translate.phrases.templates('{{label}} ({{unitSymbol}})', {
                label: translate.phrases.banyanApp('Duration'),
                unitSymbol: translate.phrases.banyanApp('Hours'),
              })}
              styles={radioStyles}
              value={DURATION}
            />
            <Radio
              label={translate.phrases.templates('{{label}} ({{unitSymbol}})', {
                label: translate.phrases.banyanApp('Volume'),
                unitSymbol: unitConverter.precipitation().suffix(),
              })}
              styles={radioStyles}
              value={VOLUME}
              key={precipitationUnit} // lets use this useSelector value to force a rerender of this component when the unit changes
            />
          </div>
        </Radio.Group>
      )}
      {selectedEmitters.length > 1 && (
        <IrrigationActivityZonesInfoText isDurationOrApplied={isDurationOrApplied} />
      )}
    </div>
  )
}
